import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Plus, Inbox, Send, ChevronLeft, RefreshCw, X, Trash2, ArrowLeft, CalendarSearch, Loader2, CalendarPlus, Clock } from 'lucide-react';
import DOMPurify from 'dompurify';

const API = process.env.REACT_APP_BACKEND_URL;

const ACCOUNT_LABELS = {
  'naledi_m@hotmail.com': 'Personal',
  'naledi@spazaglobal.com': 'Spaza Global',
  'corporate@startalentgrp.com': 'Star Talent Group',
  'team@kiddiplayclub.com': 'KiddiPlay',
  'travel@spazaluxeescapes.com': 'Spaza Luxe Escapes',
  'hello@malutiholdings.com': 'Maluti Holdings',
  'naledi@starglobalremit.com': 'Star Global Remit',
};

const EmailHub = () => {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [composeData, setComposeData] = useState({ to: '', subject: '', body: '' });
  const [sendingFrom, setSendingFrom] = useState('');
  const [viewMode, setViewMode] = useState('inbox'); // 'inbox' | 'dates' | 'sweep'
  const [dateEmails, setDateEmails] = useState([]);
  const [sweepResults, setSweepResults] = useState(null);
  const [sweeping, setSweeping] = useState(false);

  const fetchAccounts = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/email/accounts`, { credentials: 'include' });
      const data = await res.json();
      setAccounts(data.accounts || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const connectAccount = async (label) => {
    try {
      const res = await fetch(`${API}/api/email/auth/connect?label=${encodeURIComponent(label)}`, { credentials: 'include' });
      const data = await res.json();
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (e) { console.error(e); }
  };

  const isTokenExpired = (acc) => {
    // Check if backend flagged this account as needing reconnection
    if (acc.needs_reconnect) return true;
    return false;
  };

  const reAuthAccount = async (email) => {
    try {
      // Use dedicated reconnect endpoint
      const res = await fetch(`${API}/api/email/reconnect/${encodeURIComponent(email)}`, { credentials: 'include' });
      const data = await res.json();
      if (data.auth_url) window.location.href = data.auth_url;
    } catch (e) { console.error(e); }
  };

  const getAccountProvider = (email) => {
    const acc = accounts.find(a => a.email === email);
    return acc?.provider || 'microsoft';
  };

  const loadInbox = async (email) => {
    setSelectedAccount(email);
    setSelectedEmail(null);
    setViewMode('inbox');
    setLoading(true);
    try {
      const provider = getAccountProvider(email);
      const endpoint = provider === 'imap'
        ? `${API}/api/email/imap/inbox/${encodeURIComponent(email)}`
        : `${API}/api/email/inbox/${encodeURIComponent(email)}`;
      const res = await fetch(endpoint, { credentials: 'include' });
      if (res.status === 401) {
        // Token refresh failed — need re-auth
        if (window.confirm(`${email} needs re-authentication. Reconnect now?`)) {
          reAuthAccount(email);
        }
        setEmails([]);
      } else {
        const data = await res.json();
        setEmails(data.emails || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadDateEmails = async () => {
    setViewMode('dates');
    setSelectedEmail(null);
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/email/date-emails`, { credentials: 'include' });
      const data = await res.json();
      setDateEmails(data.emails || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const runSweep = async () => {
    setSweeping(true);
    setViewMode('sweep');
    try {
      const res = await fetch(`${API}/api/email/sweep-dates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ max_emails: 30 }),
      });
      const data = await res.json();
      setSweepResults(data);
    } catch (e) { console.error(e); }
    setSweeping(false);
  };

  const addToPlanner = async (item) => {
    try {
      await fetch(`${API}/api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          title: item.title,
          category: 'personal',
          due_date: item.date,
          start_time: item.time || '',
          end_time: item.end_time || '',
          notes: `${item.type} — From: ${item.source_from}\nSubject: ${item.source_subject}\n${item.description || ''}`,
          priority: 'medium',
          status: 'pending',
        }),
      });
      alert(`"${item.title}" added to planner`);
    } catch (e) { console.error(e); }
  };

  const openEmail = async (email, msgId) => {
    setLoading(true);
    try {
      const provider = getAccountProvider(email);
      const endpoint = provider === 'imap'
        ? `${API}/api/email/imap/message/${encodeURIComponent(email)}/${msgId}`
        : `${API}/api/email/message/${encodeURIComponent(email)}/${msgId}`;
      const res = await fetch(endpoint, { credentials: 'include' });
      const data = await res.json();
      setSelectedEmail(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const sendEmail = async () => {
    if (!sendingFrom || !composeData.to || !composeData.subject) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/email/send/${encodeURIComponent(sendingFrom)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(composeData),
      });
      setComposing(false);
      setComposeData({ to: '', subject: '', body: '' });
      if (selectedAccount) loadInbox(selectedAccount);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const replyToEmail = async (body) => {
    if (!selectedAccount || !selectedEmail) return;
    setLoading(true);
    try {
      await fetch(`${API}/api/email/send/${encodeURIComponent(selectedAccount)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ to: selectedEmail.from_email, subject: `Re: ${selectedEmail.subject}`, body, reply_to_id: selectedEmail.id }),
      });
      setSelectedEmail(null);
      loadInbox(selectedAccount);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const disconnectAccount = async (email) => {
    if (!window.confirm(`Disconnect ${email}?`)) return;
    await fetch(`${API}/api/email/accounts/${encodeURIComponent(email)}`, { method: 'DELETE', credentials: 'include' });
    fetchAccounts();
    if (selectedAccount === email) { setSelectedAccount(null); setEmails([]); }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const mins = Math.floor((now - d) / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const typeColors = {
    meeting: { bg: '#EFF6FF', text: '#2563EB' },
    appointment: { bg: '#ECFDF5', text: '#059669' },
    deadline: { bg: '#FEF2F2', text: '#DC2626' },
    event: { bg: '#FFFBEB', text: '#D97706' },
    reminder: { bg: '#F5F1EB', text: '#8B7355' },
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="email-hub">
      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: '#2C1A0E' }}>
        <div className="flex items-center gap-3">
          <Link to="/dashboard" className="text-white/60 hover:text-white transition-colors" data-testid="back-to-dashboard">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <Mail className="w-5 h-5" style={{ color: '#D4AF37' }} />
          <h1 className="text-sm font-medium uppercase tracking-[0.15em]" style={{ color: '#F5F0E8' }}>Email Hub</h1>
          <span className="text-xs" style={{ color: '#8B7355' }}>
            {accounts.length} account{accounts.length !== 1 ? 's' : ''} connected
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadDateEmails} className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-medium rounded-sm transition-colors hover:opacity-90" style={{ backgroundColor: viewMode === 'dates' ? '#D4AF37' : '#3A2518', color: viewMode === 'dates' ? '#2C1A0E' : '#D4AF37' }} data-testid="date-emails-btn">
            <CalendarSearch className="w-3.5 h-3.5" /> Dates & Meetings
          </button>
          <button onClick={runSweep} disabled={sweeping} className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-medium rounded-sm transition-colors hover:opacity-90 disabled:opacity-50" style={{ backgroundColor: viewMode === 'sweep' ? '#D4AF37' : '#3A2518', color: viewMode === 'sweep' ? '#2C1A0E' : '#D4AF37' }} data-testid="sweep-btn">
            {sweeping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />} {sweeping ? 'Sweeping...' : 'AI Sweep'}
          </button>
          <button onClick={() => { setComposing(true); setSendingFrom(accounts[0]?.email || ''); }} className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-wider font-medium" style={{ backgroundColor: '#D4AF37', color: '#2C1A0E' }} data-testid="compose-btn">
            <Plus className="w-3.5 h-3.5" /> Compose
          </button>
        </div>
      </div>

      <div className="flex" style={{ height: 'calc(100vh - 56px)' }}>
        {/* Sidebar — Accounts */}
        <div className="w-64 flex-shrink-0 border-r overflow-y-auto" style={{ borderColor: '#E8E0D4' }}>
          <div className="p-4">
            <p className="text-[10px] uppercase tracking-wider font-semibold mb-3" style={{ color: '#8B7355' }}>Accounts</p>
            {accounts.map(acc => {
              const expired = isTokenExpired(acc);
              return (
              <div
                key={acc.email}
                onClick={() => expired ? reAuthAccount(acc.email) : loadInbox(acc.email)}
                className={`w-full text-left px-3 py-2.5 mb-1 text-sm transition-colors group flex items-center justify-between cursor-pointer ${selectedAccount === acc.email && viewMode === 'inbox' ? 'bg-[#FAF7F2]' : 'hover:bg-[#FAFAFA]'}`}
                style={{ color: selectedAccount === acc.email ? '#2C1A0E' : '#6B5B4E', opacity: expired ? 0.7 : 1 }}
                data-testid={`account-${acc.email}`}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="text-xs font-medium truncate">{ACCOUNT_LABELS[acc.email] || acc.label}</p>
                    {acc.provider === 'imap' && <span className="text-[9px] px-1 py-0 rounded" style={{ backgroundColor: '#FFFBEB', color: '#D97706', border: '1px solid #FDE68A' }}>IMAP</span>}
                  </div>
                  <p className="text-[10px] truncate" style={{ color: '#8B7355' }}>{acc.email}</p>
                  {expired && (
                    <button onClick={(e) => { e.stopPropagation(); reAuthAccount(acc.email); }} className="mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm" style={{ backgroundColor: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }} data-testid={`reauth-${acc.email}`}>
                      Re-connect
                    </button>
                  )}
                </div>
                <button onClick={(e) => { e.stopPropagation(); disconnectAccount(acc.email); }} className="opacity-0 group-hover:opacity-100 p-1" style={{ color: '#8B7355' }}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
              );
            })}
            <button
              onClick={() => connectAccount('New Account')}
              className="w-full text-left px-3 py-2.5 mt-2 text-xs font-medium flex items-center gap-2 transition-colors hover:bg-[#FAF7F2]"
              style={{ color: '#B8860B' }}
              data-testid="connect-account-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Connect Account
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {/* AI Sweep Results View */}
          {viewMode === 'sweep' && (
            <div>
              <div className="px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E0D4' }}>
                <div className="flex items-center gap-2">
                  <CalendarPlus className="w-4 h-4" style={{ color: '#B8860B' }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#2C1A0E' }}>AI Calendar Sweep</span>
                  {sweepResults && (
                    <span className="text-[10px] px-2 py-0.5" style={{ backgroundColor: '#FAF7F2', color: '#8B7355' }}>
                      {sweepResults.items?.length || 0} items found · {sweepResults.emails_scanned || 0} emails scanned
                    </span>
                  )}
                </div>
                <button onClick={runSweep} disabled={sweeping} className="flex items-center gap-1.5 px-3 py-1.5 text-xs border rounded-sm hover:bg-amber-50 disabled:opacity-50" style={{ borderColor: '#E5E1DB', color: '#8B7355' }}>
                  {sweeping ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Re-scan
                </button>
              </div>
              {sweeping ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3">
                  <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#D4AF37' }} />
                  <p className="text-sm" style={{ color: '#8B7355' }}>Scanning your emails for dates, meetings & appointments...</p>
                  <p className="text-xs" style={{ color: '#8B7355' }}>This may take up to 30 seconds</p>
                </div>
              ) : sweepResults?.items?.length > 0 ? (
                <div className="divide-y" style={{ borderColor: '#F0EBE3' }}>
                  {sweepResults.items.map((item, i) => {
                    const tc = typeColors[item.type] || typeColors.reminder;
                    return (
                      <div key={i} className="px-6 py-4 flex items-start gap-4 hover:bg-[#FAFAFA] transition-colors" data-testid={`sweep-item-${i}`}>
                        <div className="flex-shrink-0 w-12 text-center pt-1">
                          <p className="text-lg font-light" style={{ color: '#2C1A0E' }}>{item.date ? new Date(item.date + 'T00:00:00').getDate() : '?'}</p>
                          <p className="text-[10px] uppercase" style={{ color: '#8B7355' }}>{item.date ? new Date(item.date + 'T00:00:00').toLocaleDateString('en-GB', { month: 'short' }) : ''}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{item.title}</p>
                            <span className="text-[10px] px-2 py-0.5 rounded-sm font-medium" style={{ backgroundColor: tc.bg, color: tc.text }}>{item.type?.toUpperCase()}</span>
                          </div>
                          {item.time && <p className="text-xs font-mono mb-1" style={{ color: '#8B7355' }}><Clock className="w-3 h-3 inline mr-1" />{item.time}{item.end_time ? ` — ${item.end_time}` : ''}</p>}
                          <p className="text-xs" style={{ color: '#8B7355' }}>{item.description}</p>
                          <p className="text-[10px] mt-1" style={{ color: '#B8860B' }}>From: {item.source_from} · {item.source_subject}</p>
                        </div>
                        <button onClick={() => addToPlanner(item)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border rounded-sm flex-shrink-0 hover:bg-amber-50 transition-colors" style={{ borderColor: '#E5E1DB', color: '#B8860B' }} data-testid={`add-to-planner-${i}`}>
                          <CalendarPlus className="w-3 h-3" /> Add to Planner
                        </button>
                      </div>
                    );
                  })}
                </div>
              ) : sweepResults ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: '#8B7355' }}>
                  <CalendarSearch className="w-10 h-10 opacity-30" />
                  <p className="text-sm">No date-related items found in recent emails</p>
                </div>
              ) : null}
            </div>
          )}

          {/* Date Emails View */}
          {viewMode === 'dates' && (
            <div>
              <div className="px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E0D4' }}>
                <div className="flex items-center gap-2">
                  <CalendarSearch className="w-4 h-4" style={{ color: '#B8860B' }} />
                  <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#2C1A0E' }}>Dates & Meetings</span>
                  <span className="text-[10px] px-2 py-0.5" style={{ backgroundColor: '#FAF7F2', color: '#8B7355' }}>
                    {dateEmails.length} emails
                  </span>
                </div>
                <button onClick={loadDateEmails} className="p-1.5 transition-colors hover:bg-[#FAF7F2]" style={{ color: '#8B7355' }}>
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
              {loading && dateEmails.length === 0 ? (
                <div className="flex items-center justify-center py-20">
                  <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#B8860B' }} />
                </div>
              ) : dateEmails.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 gap-3" style={{ color: '#8B7355' }}>
                  <CalendarSearch className="w-10 h-10 opacity-30" />
                  <p className="text-sm">No emails mentioning dates or meetings found</p>
                  <p className="text-xs">Make sure you have email accounts connected</p>
                </div>
              ) : (
                dateEmails.map(email => (
                  <button
                    key={`${email.account}-${email.id}`}
                    onClick={() => { setSelectedAccount(email.account); openEmail(email.account, email.id); }}
                    className="w-full text-left px-6 py-3.5 flex gap-4 transition-colors hover:bg-[#FAFAFA]"
                    style={{ borderBottom: '1px solid #F0EBE3', backgroundColor: email.is_read ? 'transparent' : '#FDFCFA' }}
                    data-testid={`date-email-${email.id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between mb-0.5">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${email.is_read ? 'font-normal' : 'font-semibold'}`} style={{ color: '#2C1A0E' }}>
                            {email.from_name || email.from_email}
                          </p>
                          <span className="text-[10px] px-1.5 py-0.5 rounded-sm" style={{ backgroundColor: '#FAF7F2', color: '#8B7355' }}>
                            {ACCOUNT_LABELS[email.account] || email.account}
                          </span>
                        </div>
                        <span className="text-[10px] flex-shrink-0 ml-3" style={{ color: '#8B7355' }}>{timeAgo(email.received_at)}</span>
                      </div>
                      <p className={`text-xs truncate mb-0.5 ${email.is_read ? '' : 'font-medium'}`} style={{ color: email.is_read ? '#6B5B4E' : '#2C1A0E' }}>
                        {email.subject}
                      </p>
                      <p className="text-xs truncate" style={{ color: '#8B7355' }}>{email.preview}</p>
                    </div>
                    {!email.is_read && <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: '#B8860B' }} />}
                  </button>
                ))
              )}
            </div>
          )}

          {/* Regular Inbox View */}
          {viewMode === 'inbox' && (
            <>
              {!selectedAccount ? (
                <div className="flex flex-col items-center justify-center h-full gap-4" style={{ color: '#8B7355' }}>
                  <Inbox className="w-12 h-12 opacity-30" />
                  <p className="text-sm">Select an account to view emails</p>
                  {accounts.length === 0 && (
                    <button onClick={() => connectAccount('First Account')} className="px-6 py-2.5 text-xs uppercase tracking-wider font-medium" style={{ backgroundColor: '#2C1A0E', color: '#F5F0E8' }} data-testid="connect-first-btn">
                      Connect Your First Account
                    </button>
                  )}
                </div>
              ) : selectedEmail ? (
                <EmailDetail email={selectedEmail} onBack={() => setSelectedEmail(null)} onReply={replyToEmail} loading={loading} />
              ) : (
                <div>
                  <div className="px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid #E8E0D4' }}>
                    <div className="flex items-center gap-2">
                      <Inbox className="w-4 h-4" style={{ color: '#B8860B' }} />
                      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#2C1A0E' }}>
                        {ACCOUNT_LABELS[selectedAccount] || selectedAccount}
                      </span>
                      <span className="text-[10px] px-2 py-0.5" style={{ backgroundColor: '#FAF7F2', color: '#8B7355' }}>
                        {emails.length} emails
                      </span>
                    </div>
                    <button onClick={() => loadInbox(selectedAccount)} className="p-1.5 transition-colors hover:bg-[#FAF7F2]" style={{ color: '#8B7355' }}>
                      <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                  {loading && emails.length === 0 ? (
                    <div className="flex items-center justify-center py-20">
                      <RefreshCw className="w-5 h-5 animate-spin" style={{ color: '#B8860B' }} />
                    </div>
                  ) : emails.length === 0 ? (
                    <div className="text-center py-20 text-sm" style={{ color: '#8B7355' }}>No emails found</div>
                  ) : (
                    emails.map(email => (
                      <button
                        key={email.id}
                        onClick={() => openEmail(selectedAccount, email.id)}
                        className="w-full text-left px-6 py-3.5 flex gap-4 transition-colors hover:bg-[#FAFAFA]"
                        style={{ borderBottom: '1px solid #F0EBE3', backgroundColor: email.is_read ? 'transparent' : '#FDFCFA' }}
                        data-testid={`email-row-${email.id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between mb-0.5">
                            <p className={`text-sm truncate ${email.is_read ? 'font-normal' : 'font-semibold'}`} style={{ color: '#2C1A0E' }}>
                              {email.from_name || email.from_email}
                            </p>
                            <span className="text-[10px] flex-shrink-0 ml-3" style={{ color: '#8B7355' }}>{timeAgo(email.received_at)}</span>
                          </div>
                          <p className={`text-xs truncate mb-0.5 ${email.is_read ? '' : 'font-medium'}`} style={{ color: email.is_read ? '#6B5B4E' : '#2C1A0E' }}>
                            {email.subject}
                          </p>
                          <p className="text-xs truncate" style={{ color: '#8B7355' }}>{email.preview}</p>
                        </div>
                        {!email.is_read && <div className="w-2 h-2 rounded-full flex-shrink-0 mt-2" style={{ backgroundColor: '#B8860B' }} />}
                      </button>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Compose Modal */}
      {composing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(44,26,14,0.5)' }}>
          <div className="bg-white max-w-lg w-full shadow-2xl" data-testid="compose-modal">
            <div className="px-5 py-3 flex items-center justify-between" style={{ backgroundColor: '#2C1A0E' }}>
              <p className="text-xs uppercase tracking-wider font-medium" style={{ color: '#D4AF37' }}>New Email</p>
              <button onClick={() => setComposing(false)}><X className="w-4 h-4" style={{ color: '#8B7355' }} /></button>
            </div>
            <div className="p-5 flex flex-col gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold block mb-1" style={{ color: '#8B7355' }}>From</label>
                <select value={sendingFrom} onChange={e => setSendingFrom(e.target.value)} className="w-full px-3 py-2 border text-sm" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} data-testid="compose-from">
                  {accounts.map(a => <option key={a.email} value={a.email}>{ACCOUNT_LABELS[a.email] || a.email} ({a.email})</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold block mb-1" style={{ color: '#8B7355' }}>To</label>
                <input value={composeData.to} onChange={e => setComposeData({ ...composeData, to: e.target.value })} className="w-full px-3 py-2 border text-sm" style={{ borderColor: '#E8E0D4' }} placeholder="recipient@email.com" data-testid="compose-to" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold block mb-1" style={{ color: '#8B7355' }}>Subject</label>
                <input value={composeData.subject} onChange={e => setComposeData({ ...composeData, subject: e.target.value })} className="w-full px-3 py-2 border text-sm" style={{ borderColor: '#E8E0D4' }} placeholder="Subject" data-testid="compose-subject" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-semibold block mb-1" style={{ color: '#8B7355' }}>Message</label>
                <textarea value={composeData.body} onChange={e => setComposeData({ ...composeData, body: e.target.value })} className="w-full px-3 py-2 border text-sm h-40 resize-none" style={{ borderColor: '#E8E0D4' }} placeholder="Write your message..." data-testid="compose-body" />
              </div>
              <button onClick={sendEmail} disabled={loading} className="flex items-center justify-center gap-2 px-6 py-3 text-xs uppercase tracking-wider font-medium" style={{ backgroundColor: '#2C1A0E', color: '#F5F0E8' }} data-testid="compose-send">
                <Send className="w-3.5 h-3.5" /> {loading ? 'Sending...' : 'Send'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* Email Detail View */
const EmailDetail = ({ email, onBack, onReply, loading }) => {
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);

  return (
    <div>
      <div className="px-6 py-3 flex items-center gap-3" style={{ borderBottom: '1px solid #E8E0D4' }}>
        <button onClick={onBack} className="p-1 transition-colors hover:bg-[#FAF7F2]" style={{ color: '#8B7355' }}>
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-xs uppercase tracking-wider font-medium" style={{ color: '#8B7355' }}>Back to Inbox</span>
      </div>
      <div className="px-6 py-6">
        <h2 className="text-xl font-light mb-4 tracking-tight" style={{ color: '#2C1A0E' }}>{email.subject}</h2>
        <div className="flex items-center gap-3 mb-6 pb-4" style={{ borderBottom: '1px solid #F0EBE3' }}>
          <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#FAF7F2', color: '#B8860B' }}>
            {(email.from_name || email.from_email || '?')[0].toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium" style={{ color: '#2C1A0E' }}>{email.from_name}</p>
            <p className="text-xs" style={{ color: '#8B7355' }}>{email.from_email}</p>
          </div>
          <p className="ml-auto text-xs" style={{ color: '#8B7355' }}>
            {email.received_at ? new Date(email.received_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
          </p>
        </div>
        <div className="text-sm leading-[1.8] mb-8" style={{ color: '#4A3A2E' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(email.body || '') }} />
        {!showReply ? (
          <button onClick={() => setShowReply(true)} className="flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-wider font-medium border transition-colors hover:bg-[#FAF7F2]" style={{ borderColor: '#E8E0D4', color: '#2C1A0E' }} data-testid="reply-btn">
            Reply
          </button>
        ) : (
          <div className="p-4" style={{ backgroundColor: '#FAF7F2', border: '1px solid #E8E0D4' }}>
            <textarea value={replyText} onChange={e => setReplyText(e.target.value)} className="w-full px-3 py-2 border text-sm h-32 resize-none bg-white mb-3" style={{ borderColor: '#E8E0D4' }} placeholder="Write your reply..." data-testid="reply-textarea" />
            <div className="flex gap-2">
              <button onClick={() => { onReply(replyText); setShowReply(false); setReplyText(''); }} disabled={loading || !replyText} className="flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-wider font-medium" style={{ backgroundColor: '#2C1A0E', color: '#F5F0E8' }} data-testid="send-reply-btn">
                <Send className="w-3 h-3" /> {loading ? 'Sending...' : 'Send Reply'}
              </button>
              <button onClick={() => setShowReply(false)} className="px-4 py-2 text-xs" style={{ color: '#8B7355' }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailHub;
